import { useEffect, useState } from 'react'
import moment from 'moment'
import { OnsiteResultsPressureGraphResponseData } from '../../types/typescript-axios'
import { DefaultWithTitleLayout } from '../../components/templates/default-with-title-layout'
import { Lang } from '../../translations/lang'
import { webRequest } from '../../types/handle-api'
import { SearchButton } from '../../components/part/search-button'
import {
  AdminPressureGraphFilteringModal,
  AdminPressureGraphFilteringModalHideArgs,
} from '../../components/templates/admin-pressure-graph/admin-pressure-graph-filtering-modal'
import { useForm } from 'react-hook-form'
import { AdminPressureGraphEntity } from '../../components/templates/admin-pressure-graph/admin-pressure-graph-entity'

export type PressureGraphYAxisType = 'pressureValue' | 'pressureIncrease'
export type PressureGraphXAxisType = 'processingDate' | 'elapsedDate'

export type PressureGraphData = {
  id: string
  vixellId: number
  firstPressure: number
  stockedDateTime: string | undefined
  lastProcessingDateTime: string | undefined
  data: { x: number; y: number }[]
}

export type SelectedVixell = {
  vixellId: number
  vixellSerialNo: string
}

/**
 * 期間未指定だと OnsiteVacuumResult を全件返してしまい、Lambda の
 * レスポンス上限(6MB)/タイムアウトを超過して圧力グラフ画面が無応答になる。
 * これを防ぐため、初期表示で「直近 N ヶ月〜今日」を期間フィルタの初期値にする。
 * この値は serverless 側の安全弁（期間未指定時のクランプ）と揃えること。
 */
const DEFAULT_STOCKED_PERIOD_MONTHS = 3

export const AdminPressureGraph = () => {
  const form = useForm<AdminPressureGraphFilteringModalHideArgs>({
    defaultValues: {
      stockedFrom: moment()
        .subtract(DEFAULT_STOCKED_PERIOD_MONTHS, 'months')
        .format('YYYY-MM-DD'),
      stockedTill: moment().format('YYYY-MM-DD'),
    },
  })
  const [currentFilterValues, setCurrentFilterValues] =
    useState<AdminPressureGraphFilteringModalHideArgs>(form.getValues())
  const [graphData, setGraphData] =
    useState<OnsiteResultsPressureGraphResponseData>()
  const [showFilteringModal, setShowFilteringModal] = useState(false)

  useEffect(() => {
    async function fetchData() {
      const { vixellSerialNo, model, contractNo, stockedFrom, stockedTill } =
        currentFilterValues
      const response = await webRequest('OnsiteResultsPressureGraph', {
        ...(vixellSerialNo && { vixellSerialNo }),
        ...(model && { model }),
        ...(contractNo && { contractNo }),
        ...(stockedFrom && { stockedFrom }),
        ...(stockedTill && { stockedTill }),
      })
      if (response.status === 200) {
        setGraphData(response.data)
      }
    }

    fetchData()
  }, [currentFilterValues])

  function executeFilter(shouldUpdateValues: boolean) {
    if (shouldUpdateValues) setCurrentFilterValues(form.getValues())
    setShowFilteringModal(false)
  }

  const {
    stockedFrom,
    stockedTill,
    model,
    yAxisUpper,
    yAxisLower,
    xAxisUpper,
    xAxisLower,
    xAxisUpperDate,
    xAxisLowerDate,
    xRuledLineRange,
    yRuledLineRange,
  } = currentFilterValues

  return (
    <DefaultWithTitleLayout
      title={Lang.header.pressureGraph}
      titleRightElement={
        <SearchButton
          label={Lang.filtering}
          onClick={() => setShowFilteringModal(true)}
        />
      }
    >
      {graphData && (
        <AdminPressureGraphEntity
          stockedFrom={stockedFrom}
          stockedTill={stockedTill}
          model={model}
          yAxisUpper={yAxisUpper}
          yAxisLower={yAxisLower}
          xAxisUpper={xAxisUpper}
          xAxisLower={xAxisLower}
          xAxisUpperDate={xAxisUpperDate}
          xAxisLowerDate={xAxisLowerDate}
          graphData={graphData}
          yRuledLineRange={yRuledLineRange}
          xRuledLineRange={xRuledLineRange}
        />
      )}
      {showFilteringModal && (
        <AdminPressureGraphFilteringModal form={form} hide={executeFilter} />
      )}
    </DefaultWithTitleLayout>
  )
}
